import { Component, inject, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { HandleLoadingState } from '../../decorators/handle-loading-state.decorator';
import { ClientDiaryDataService } from '../../services/todo/client-diary-data.service';
import { PersonalTaskDataService } from '../../services/todo/personal-task-data.service';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { TagComponent } from '../core/labels/tag.component';
import { LoadingPanelComponent } from '../core/panels/loading-panel.component';
import { HomeCardComponent } from './home-card.component';

@Component({
  selector: 'app-tasks-card',
  imports: [
    HomeCardComponent,
    ButtonComponent,
    TagComponent,
    RouterLink,
    LoadingPanelComponent,
  ],
  template: `
    <app-home-card
      type="Large"
      theme="Light"
      [padding]="false"
      [minHeight]="'420px'"
      classes="md:tw-min-h-[420px]"
    >
      <app-loading-panel
        loadingStyle="Box"
        [loadingHeight]="380"
        [state]="loading ? 'Loading' : 'Loaded'"
        [error]="error"
      >
        <ng-template #loaded>
          <div
            class="tasks-diary-row tw-flex tw-pb-8"
            [class.tw-divide-x]="sharedDiariesAuthorize"
            [class.tw-divide-gray-300]="sharedDiariesAuthorize"
            [class.tw-justify-center]="!sharedDiariesAuthorize"
          >
            <div
              [class.tw-w-1/2]="sharedDiariesAuthorize"
              [class.tw-w-full]="!sharedDiariesAuthorize"
              [class.tw-pr-4]="sharedDiariesAuthorize"
              [class.md:tw-pr-8]="sharedDiariesAuthorize"
              [class.tw-max-w-md]="!sharedDiariesAuthorize"
            >
              <div class="tw-flex tw-font-bold tw-pb-4">
                <h4>Tasks</h4>
              </div>
              <div class="tw-flex tw-flex-col tw-gap-4 tw-pb-4">
                <div class="due-today-hero tw-flex tw-flex-col tw-items-start tw-gap-1">
                  <span
                    class="tw-shrink-0 tw-font-bold tw-text-[var(--sed-purple-80-color)] tw-text-[80px] md:tw-text-[160px] tw-leading-none"
                    [attr.aria-label]="getHeroCountAriaLabel(tasks.total)"
                  >
                    {{ tasks.total > 99 ? '99+' : tasks.total }}
                  </span>
                  <span class="due-today-words tw-font-bold tw-text-[17px] tw-sentence-case">
                    {{ getTasksDueTodayLabel(tasks.total) }}
                  </span>
                </div>
                <div class="tw-flex tw-flex-col tw-gap-4">
                  <div class="tw-flex tw-flex-row tw-gap-2">
                    <span class="tw-text-[17px]">{{
                      tasks.sevenDays + getTasksLabel(tasks.sevenDays)
                    }}</span>
                    <app-tag type="Grey">
                      <div class="tw-sentence-case tw-text-[14px] tw-pt-1">
                        Due in 7 days
                      </div>
                    </app-tag>
                  </div>
                  @if (tasks.pastDue > 0) {
                    <div class="tw-flex tw-flex-row tw-gap-2">
                      <span class="tw-text-[17px]">{{
                        tasks.pastDue + getTasksLabel(tasks.pastDue)
                      }}</span>
                      <app-tag type="Error">
                        <div class="tw-sentence-case tw-text-[14px] tw-pt-1">
                          Past due
                        </div>
                      </app-tag>
                    </div>
                  }
                </div>
              </div>
              <div class="tw-flex tw-pt-4 tw-py-4">
                <app-button
                  label="View tasks"
                  [routerLink]="['/todo']"
                  [queryParams]="{ tab: 'Personal tasks' }"
                />
              </div>
            </div>
            @if (sharedDiariesAuthorize) {
              <div class="tw-w-1/2 tw-pl-4 md:tw-pl-8">
                <div class="tw-flex tw-font-bold tw-pb-4">
                  <h4>Diaries</h4>
                </div>
                <div class="tw-flex tw-flex-col tw-gap-4 tw-pb-4">
                  <div class="due-today-hero tw-flex tw-flex-col tw-items-start tw-gap-1">
                    <span
                      class="tw-shrink-0 tw-font-bold tw-text-[var(--sed-purple-80-color)] tw-text-[80px] md:tw-text-[160px] tw-leading-none"
                      [attr.aria-label]="getHeroCountAriaLabel(diaries.total)"
                    >
                      {{ diaries.total > 99 ? '99+' : diaries.total }}
                    </span>
                    <span class="due-today-words tw-font-bold tw-text-[17px] tw-sentence-case">
                      {{ getDiariesDueTodayLabel(diaries.total) }}
                    </span>
                  </div>
                  <div class="tw-flex tw-flex-col tw-gap-4">
                    <div class="tw-flex tw-flex-row tw-gap-2">
                      <span class="tw-text-[17px]">{{
                        diaries.sevenDays + getDiariesLabel(diaries.sevenDays)
                      }}</span>
                      <app-tag type="Grey">
                        <div class="tw-sentence-case tw-text-[14px] tw-pt-1">
                          Due in 7 days
                        </div>
                      </app-tag>
                    </div>
                    @if (diaries.pastDue > 0) {
                      <div class="tw-flex tw-flex-row tw-gap-2">
                        <span class="tw-text-[17px]">{{
                          diaries.pastDue + getDiariesLabel(diaries.pastDue)
                        }}</span>
                        <app-tag type="Error">
                          <div class="tw-sentence-case tw-text-[14px] tw-pt-1">
                            Past due
                          </div>
                        </app-tag>
                      </div>
                    }
                  </div>
                </div>
                <div class="tw-flex tw-py-4">
                  <app-button
                    label="View diaries"
                    [routerLink]="['/todo']"
                    [queryParams]="{ tab: 'Shared diaries' }"
                  />
                </div>
              </div>
            }
          </div>
        </ng-template>
      </app-loading-panel>
    </app-home-card>
  `,
  styles: `
    :host {
      display: block;
    }

    .due-today-hero {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      row-gap: 0.25rem;
      min-height: 100px;
    }

    .due-today-words {
      font-weight: 700;
      text-align: left;
    }

    @media (max-width: 768px) {
      :host ::ng-deep .tasks-diary-row {
        flex-wrap: nowrap !important;
      }
    }

    @media (min-width: 768px) {
      .due-today-hero {
        min-height: 180px;
      }
    }
  `,
})
export class TasksCardComponent extends BaseComponent implements OnInit {
  private clientDiaryData = inject(ClientDiaryDataService);
  private personalTaskData = inject(PersonalTaskDataService);

  sharedDiariesAuthorize = false;
  loading = false;
  error: unknown = null;

  tasks = {
    total: 0,
    pastDue: 0,
    sevenDays: 0,
  };

  diaries = {
    total: 0,
    pastDue: 0,
    sevenDays: 0,
  };

  getMessagesLabel(messages: number): string {
    return messages > 1 ? `${messages} messages` : `${messages} message`;
  }

  getTasksLabel(tasks: number): string {
    return tasks === 1 ? ' task' : ' tasks';
  }

  getTasksDueTodayLabel(count: number): string {
    return count === 1 ? 'Task due today' : 'Tasks due today';
  }

  getHeroCountAriaLabel(count: number): string | null {
    return count > 99 ? '99 or more' : null;
  }

  constructor() {
    super();
    this.dispose.subs = this.clientDiaryData.diariesChanged.subscribe(() => {
      this.loadDiariesCount(true);
    });
    this.dispose.subs = this.personalTaskData.tasksChanged.subscribe(() => {
      this.loadTaskCount(true);
    });
  }

  getDiariesLabel(diaries: number): string {
    return diaries === 1 ? ' diary' : ' diaries';
  }

  getDiariesDueTodayLabel(count: number): string {
    return count === 1 ? 'Diary due today' : 'Diaries due today';
  }

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    this.sharedDiariesAuthorize =
      !this.session.isSpecificClaimUser &&
      this.session.authorize('ClientDiaryModule');
    await this.loadCounts();
  }

  @HandleLoadingState()
  async loadCounts(): Promise<void> {
    await Promise.all([this.loadTaskCount(), this.loadDiariesCount()]);
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async loadDiariesCount(refreshCache = false) {
    if (this.sharedDiariesAuthorize) {
      const diaryCountResponse =
        await this.api.clientDiary.getDiaryCount(refreshCache);
      this.diaries = {
        total: diaryCountResponse.dueToday ?? 0,
        pastDue: diaryCountResponse.duePast || 0,
        sevenDays: diaryCountResponse.dueThisWeek ?? 0,
      };
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async loadTaskCount(refreshCache = false) {
    const taskCountResponse = await this.api.task.getTaskCount(refreshCache);
    this.tasks = {
      total: taskCountResponse.dueToday ?? 0,
      pastDue: taskCountResponse.duePast || 0,
      sevenDays: taskCountResponse.dueThisWeek ?? 0,
    };
  }
}
